// frontend\src\pages\DevTimerSetup\StageCard.js
import React, { useRef } from "react";
import PropTypes from "prop-types";
import { AGITATION_TYPES } from "./constants";

const StageCard = ({
  stageKey,
  params,
  toggleStage,
  handleChange,
  handleAgitationChange,
  handleAgitationTypeChange,
  errors,
}) => {
  const stageErrors = errors[stageKey] || [];
  const totalSeconds = params.minutes * 60 + params.seconds;
  const showFirstLastMinute = totalSeconds >= 180;

  // Refs для полей
  const minutesRef = useRef(null);
  const secondsRef = useRef(null);
  const intervalRef = useRef(null);
  const durationRef = useRef(null);
  const firstMinuteRef = useRef(null);
  const lastMinuteRef = useRef(null);

  // Прямые обработчики — без debounce
  const onMinutesChange = (e) => {
    const value = Math.max(0, Math.min(120, parseInt(e.target.value) || 0));
    handleChange(stageKey, "minutes", value);
  };

  const onSecondsChange = (e) => {
    const value = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
    handleChange(stageKey, "seconds", value);
  };

  const onIntervalChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    handleAgitationChange(stageKey, "interval", value);
  };

  const onDurationChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    handleAgitationChange(stageKey, "duration", value);
  };

  const onFirstMinuteChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    handleAgitationChange(stageKey, "firstMinute", value);
  };

  const onLastMinuteChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    handleAgitationChange(stageKey, "lastMinute", value);
  };

  const onAgitationTypeChange = (e) => {
    handleAgitationTypeChange(stageKey, e.target.value);
  };

  return (
    <div className={`card mb-3 ${stageErrors.length ? "border-danger" : ""}`}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={params.enabled}
            onChange={() => toggleStage(stageKey)}
            style={{ transform: "scale(1.5)" }}
          />
          <label className="form-check-label h5 mb-0 ms-2 text-primary">
            {params.name}
          </label>
        </div>
      </div>

      {params.enabled && (
        <div className="card-body">
          {/* Ошибки */}
          {stageErrors.length > 0 && (
            <div className="alert alert-danger">
              <ul className="mb-0">
                {stageErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-muted">{params.description}</p>

          {/* Время */}
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Минуты</label>
              <input
                ref={minutesRef}
                type="number"
                className="form-control"
                value={params.minutes}
                onChange={onMinutesChange}
                min="0"
                max="120"
                inputMode="numeric"
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Секунды</label>
              <input
                ref={secondsRef}
                type="number"
                className="form-control"
                value={params.seconds}
                onChange={onSecondsChange}
                min="0"
                max="59"
                inputMode="numeric"
              />
            </div>
          </div>

          {/* Агитация */}
          <div className="mt-4">
            <h6 className="border-bottom pb-2">Настройки агитации</h6>

            <div className="row g-3 align-items-center">
              <div className="col-12">
                <label className="form-label">Тип агитации</label>
                <select
                  className="form-select"
                  value={params.agitationType}
                  onChange={onAgitationTypeChange}
                >
                  <option value={AGITATION_TYPES.STANDARD}>
                    Стандартная (периодическая)
                  </option>
                  <option value={AGITATION_TYPES.ROTARY}>
                    Ротационная (непрерывная)
                  </option>
                  <option value={AGITATION_TYPES.STAND}>
                    Без агитации (stand development)
                  </option>
                </select>
              </div>

              {/* STANDARD */}
              {params.agitationType === AGITATION_TYPES.STANDARD && (
                <>
                  <div className="col-md-6">
                    <label className="form-label">Интервал (сек)</label>
                    <input
                      ref={intervalRef}
                      type="number"
                      className="form-control"
                      value={params.agitation?.interval ?? ""}
                      onChange={onIntervalChange}
                      min="5"
                      max="300"
                      placeholder="60"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Длительность (сек)</label>
                    <input
                      ref={durationRef}
                      type="number"
                      className="form-control"
                      value={params.agitation?.duration ?? ""}
                      onChange={onDurationChange}
                      min="3"
                      max="60"
                      placeholder="10"
                    />
                  </div>

                  {showFirstLastMinute && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label">Первая минута (сек)</label>
                        <input
                          ref={firstMinuteRef}
                          type="number"
                          className="form-control"
                          value={params.agitation?.firstMinute ?? ""}
                          onChange={onFirstMinuteChange}
                          min="5"
                          max="60"
                          placeholder="30"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Последняя минута (сек)</label>
                        <input
                          ref={lastMinuteRef}
                          type="number"
                          className="form-control"
                          value={params.agitation?.lastMinute ?? ""}
                          onChange={onLastMinuteChange}
                          min="5"
                          max="60"
                          placeholder="15"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ROTARY */}
              {params.agitationType === AGITATION_TYPES.ROTARY && (
                <div className="col-md-6">
                  <label className="form-label">Длительность (сек)</label>
                  <input
                    ref={durationRef}
                    type="number"
                    className="form-control"
                    value={params.agitation?.duration ?? ""}
                    onChange={onDurationChange}
                    min="3"
                    max="60"
                    placeholder="195"
                  />
                </div>
              )}
            </div>
          </div>

          {/* DEBUG: */}
          {/* {process.env.NODE_ENV === "development" && (
            <details className="mt-3">
              <summary className="text-muted small">Debug: agitation</summary>
              <pre className="small bg-light p-2 rounded">
                {JSON.stringify(params.agitation, null, 2)}
              </pre>
            </details>
          )} */}
        </div>
      )}
    </div>
  );
};

StageCard.propTypes = {
  stageKey: PropTypes.string.isRequired,
  params: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    enabled: PropTypes.bool,
    minutes: PropTypes.number,
    seconds: PropTypes.number,
    agitationType: PropTypes.string,
    agitation: PropTypes.object,
  }).isRequired,
  toggleStage: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleAgitationChange: PropTypes.func.isRequired,
  handleAgitationTypeChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default React.memo(StageCard);